import React, { useState } from 'react';
import { CONTRACTS, CHAIN_IDS, LINKS } from '../../data/site';
import { cn } from '../../lib/cn';
import boopImage from '../../assets/images/boop.jpg';

const TokenPill = ({ icon, name, chain }) => (
  <span className="flex shrink-0 items-center gap-2 rounded-full bg-sand py-1.5 pl-1.5 pr-3">
    {icon}
    <span className="text-left leading-[1.1]">
      <span className="block text-base font-bold">{name}</span>
      <span className="block text-xs font-medium text-muted">{chain}</span>
    </span>
  </span>
);

const Box = ({ label, badge, children }) => (
  <div className="mx-3.5 rounded-2xl border-[3px] border-ink bg-paper px-3.5 py-3 lg:mx-5">
    <div className="mb-1.5 flex items-center justify-between gap-2">
      <span className="text-[15px] font-medium text-muted">{label}</span>
      {badge ? <span className="rounded-full border-2 border-ink bg-sun px-3 py-0.5 text-[12px] font-semibold">{badge}</span> : null}
    </div>
    <div className="flex items-center justify-between gap-2">{children}</div>
  </div>
);

/**
 * Buy card: pick a chain, then hand off to relay.link with $BOOP preselected.
 * No wallet connection and no prices are faked here; quotes live on Relay.
 */
const BuyCard = ({ initialChain = 'arbitrum' }) => {
  const [chainId, setChainId] = useState(initialChain);
  const chain = CONTRACTS[chainId];

  return (
    <div className="overflow-hidden rounded-[22px] border-[3px] border-ink bg-paper shadow-hard-lg lg:rounded-[26px]">
      <div className="px-4 pt-3.5 lg:px-6 lg:pt-5">
        <div className="flex items-center justify-between text-xs font-bold tracking-[2px]">
          <span className="flex items-center">
            <span className="mr-2 inline-block h-3 w-3 rounded-full border-2 border-ink bg-sun" aria-hidden="true" />
            {chain.name.toUpperCase()}
          </span>
          <span className="text-sm font-medium tracking-normal text-muted">via Relay</span>
        </div>
        <h2 className="mb-3 mt-1.5 text-[34px] font-bold tracking-[0.5px] lg:text-[40px]">Buy $BOOP</h2>
      </div>

      <div className="mb-3 flex justify-center gap-2" role="group" aria-label="Choose chain">
        {CHAIN_IDS.map((id) => {
          const active = id === chainId;
          return (
            <button
              key={id}
              type="button"
              aria-pressed={active}
              onClick={() => setChainId(id)}
              className={cn(
                'cursor-pointer rounded-full border-[3px] border-ink px-[18px] py-1.5 text-sm font-bold shadow-[3px_3px_0_#1c1410] active:translate-x-0.5 active:translate-y-0.5 active:shadow-[1px_1px_0_#1c1410]',
                active ? 'bg-ink text-sun' : 'bg-paper text-ink'
              )}
            >
              {CONTRACTS[id].name}
            </button>
          );
        })}
      </div>

      <Box label="Sell">
        <span className="min-w-0 text-[22px] font-semibold leading-tight text-muted/80">Any amount</span>
        <TokenPill
          icon={
            <span className="flex h-[38px] w-[38px] items-center justify-center rounded-full border-2 border-ink bg-sun text-xl font-bold">
              {chain.payIcon}
            </span>
          }
          name={chain.payToken}
          chain={chain.name}
        />
      </Box>

      <div className="relative z-10 -my-2 flex justify-center" aria-hidden="true">
        <span className="flex h-[30px] w-[30px] items-center justify-center rounded-full border-2 border-ink bg-sand text-sm font-bold">⇅</span>
      </div>

      <Box label="Buy" badge="To your wallet">
        <span className="min-w-0 text-[22px] font-semibold leading-tight">$BOOP</span>
        <TokenPill
          icon={<img src={boopImage} alt="" className="h-[38px] w-[38px] rounded-full border-2 border-ink object-cover" width={38} height={38} />}
          name="BOOP"
          chain={chain.name}
        />
      </Box>

      <p className="mx-4 mt-2.5 text-center text-[13px] font-medium text-muted lg:mx-6">
        Amounts, live quotes and wallet connection happen on relay.link. This site never asks for your wallet.
      </p>

      <div className="px-3.5 pt-3 lg:px-5">
        <a className="btn w-full py-4 text-xl tracking-[2px]" href={chain.relay} target="_blank" rel="noopener noreferrer">
          BUY ON RELAY ↗
        </a>
      </div>
      <div className="px-3.5 pt-3 lg:px-5">
        <a className="btn btn-paper w-full justify-between text-[17px]" href={chain.explorer} target="_blank" rel="noopener noreferrer">
          <span>View on {chain.explorerName}</span>
          <span aria-hidden="true">↗</span>
        </a>
      </div>
      <div className="flex justify-between gap-2 px-4 pb-3.5 pt-3 text-sm font-medium lg:px-6 lg:pb-5">
        <span>
          {chain.short} · {chain.standard}
        </span>
        <a className="underline" href={LINKS.coingecko} target="_blank" rel="noopener noreferrer">
          Price on CoinGecko ↗
        </a>
      </div>
    </div>
  );
};

export default BuyCard;
